/**
 * rerunVersionNote — generate a human-readable rerun version note from
 * the RR1 delta + a config-diff summary.
 *
 * Rerun Reimagined RR6. Replaces the always-empty free-text "version
 * note" field on the legacy Review step with a pre-filled string the
 * user can edit. Saved on rerun via `regenerationReason` (Workflow
 * Prompt 6), so the version history becomes searchable instead of
 * all-blank.
 *
 * Pure function — no React, no IO. The caller composes the delta and
 * config-diff inputs from store state and passes them in.
 */
import type { RerunDelta } from './rerunDelta';

export interface RerunConfigDiff {
  /** Display labels of fields the user edited (e.g. "Power requirement (MW)"). */
  changedFields: ReadonlyArray<string>;
}

export interface GenerateVersionNoteContext {
  /** Pinned SED version on the parent report. Optional — only used for the SED label. */
  pinnedSedVersion?: number;
  /** Live site SED version. Optional — only used for the SED label. */
  liveSedVersion?: number;
}

function plural(n: number): string {
  return n === 1 ? '' : 's';
}

/**
 * Build a short, pipe-separated summary of what changed.
 *
 * Examples:
 *   "1 new doc · SED v2→v3 · 2 gaps resolved"
 *   "edited Power requirement (MW), Cooling preference"
 *   "Manual rerun (no detectable changes)"
 */
export function generateVersionNote(
  delta: RerunDelta,
  configDiff: RerunConfigDiff,
  context: GenerateVersionNoteContext = {},
): string {
  const parts: string[] = [];

  if (delta.newDocuments.length > 0) {
    parts.push(`${delta.newDocuments.length} new doc${plural(delta.newDocuments.length)}`);
  }

  if (delta.sedVersionDrift != null && delta.sedVersionDrift !== 0) {
    if (context.pinnedSedVersion != null && context.liveSedVersion != null) {
      parts.push(`SED v${context.pinnedSedVersion}→v${context.liveSedVersion}`);
    } else {
      const sign = delta.sedVersionDrift > 0 ? '+' : '';
      parts.push(`SED Δ${sign}${delta.sedVersionDrift}`);
    }
  }

  if (delta.providerKeysAdded.length > 0) {
    parts.push(
      `${delta.providerKeysAdded.length} provider key${plural(delta.providerKeysAdded.length)}`,
    );
  }

  if (delta.resolvedGaps > 0) {
    parts.push(`${delta.resolvedGaps} gap${plural(delta.resolvedGaps)} resolved`);
  }

  if (configDiff.changedFields.length > 0) {
    // Truncate long lists to keep the note scannable. Pivot to a count
    // tail when we exceed 3 fields.
    if (configDiff.changedFields.length <= 3) {
      parts.push(`edited ${configDiff.changedFields.join(', ')}`);
    } else {
      const head = configDiff.changedFields.slice(0, 3).join(', ');
      const rest = configDiff.changedFields.length - 3;
      parts.push(`edited ${head} +${rest} more`);
    }
  }

  if (parts.length === 0) return 'Manual rerun (no detectable changes)';
  return parts.join(' · ');
}
